/**
 * Sawyer 11 client-notes-agreements (F1) — the "New client note" flag.
 *
 * Sawyer: "if new notes are made, some kind of flag to say there's a new
 * note on this" (memo2 [29:10]). One batched signals read per page (never per
 * row, P4), shared by every row of that page through context:
 *
 *  - `ClientNoteSignalsProvider` fetches for the page's customer ids, again
 *    whenever the page's own refresh key moves (a check-in, a shell booking
 *    change — refuter C8 / MAC4), and only for roles holding
 *    `customers:read:all` (a groomer's page sends nothing, AC15).
 *  - `ClientNoteChip` sits after the owner's flags: "New client note" /
 *    "N new client notes" (warning tone), else "Pinned note" (neutral).
 *    `toggle` opens the row's digest (Front Desk); `link` goes to the client
 *    (Dashboard).
 *  - `ClientNotesRowSlot` renders the digest under the row that is open.
 *
 * Opening a row marks the client's notes seen for me — but only when the
 * digest shows EVERY new note (AC14); otherwise they clear when the client
 * page loads. The chip clears for this page view immediately; a failed POST
 * is swallowed (MAC5): the server never recorded it, so the chip returns on
 * the next load, which is the honest outcome.
 *
 * Deploy window / failure (AC18): any failed or unmapped read leaves an
 * empty map, so rows render exactly as before, with no placeholder, no
 * layout shift, no console output, and never a claim of "no notes".
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type JSX,
  type KeyboardEvent,
  type ReactNode,
} from 'react';
import { Link } from 'react-router-dom';
import { readSession } from '../../lib/api.js';
import {
  fetchClientNoteSignals,
  markCustomerNotesSeen,
  type ClientNoteSignal,
} from '../../lib/client-notes-api.js';
import { effectiveRolesOf, rolesHavePermission } from '../../lib/rbac-matrix.js';
import { ClientNotesDigest } from './ClientNotesDigest.js';
import './ClientNotes.css';

/** The permission every client-note read rides (owner, manager, receptionist, accountant). */
export function canReadClientNotes(): boolean {
  return rolesHavePermission(effectiveRolesOf(readSession().staff), 'customers:read:all');
}

/** Newest `created_at` among the notes the digest shows — the "seen" watermark. */
export function seenThroughFor(signal: ClientNoteSignal): string | null {
  let best: string | null = null;
  for (const n of signal.new_notes) {
    if (best === null || Date.parse(n.created_at) > Date.parse(best)) best = n.created_at;
  }
  return best;
}

/** True when the digest can show every note new to me (AC14). */
export function digestShowsEveryNewNote(signal: ClientNoteSignal): boolean {
  return signal.unseen_count > 0 && signal.unseen_count <= signal.new_notes.length;
}

/** "New client note" / "2 new client notes" / "Pinned note" / null. */
export function clientNoteChipLabel(
  signal: Pick<ClientNoteSignal, 'unseen_count' | 'pinned_total'> | undefined,
): string | null {
  if (!signal) return null;
  if (signal.unseen_count === 1) return 'New client note';
  if (signal.unseen_count > 1) return `${signal.unseen_count} new client notes`;
  if (signal.pinned_total > 0) return 'Pinned note';
  return null;
}

/**
 * The toggle chip's label (MAC10, review round 1). The flag itself follows
 * `clientNoteChipLabel`. Beyond it, the row a staffer opened keeps a neutral
 * "Client notes" chip for the rest of this page view, so long as there is
 * something to reopen: while open it is the way to close the row, and after
 * Escape or a second press it is where keyboard focus still sits. Without
 * this, a client with no pinned note lost the chip (and focus, to <body>) on
 * collapse. Every other row of that client still clears (AC11), and a fresh
 * load shows only what the server says.
 */
export function toggleChipLabel(
  signal: ClientNoteSignal | undefined,
  row: { expanded: boolean; opened: boolean },
): string | null {
  const flag = clientNoteChipLabel(signal);
  if (flag) return flag;
  if (row.expanded) return 'Client notes';
  const reopenable = signal !== undefined && (signal.pinned.length > 0 || signal.new_notes.length > 0);
  return row.opened && reopenable ? 'Client notes' : null;
}

/**
 * One client's signals, for a single-client surface (the client page band,
 * the dog profile). `null` until loaded, on failure, or when gated out.
 */
export function useClientNoteSignal(
  apiUrl: string,
  customerId: string | null | undefined,
  refreshKey?: unknown,
): { signal: ClientNoteSignal | null; markSeen: () => void } {
  const [signal, setSignal] = useState<ClientNoteSignal | null>(null);
  const allowed = canReadClientNotes();
  useEffect(() => {
    if (!allowed || !customerId) {
      setSignal(null);
      return;
    }
    let live = true;
    void fetchClientNoteSignals(apiUrl, [customerId]).then((r) => {
      if (!live) return;
      setSignal(r.kind === 'ok' ? (r.signals.find((s) => s.customer_id === customerId) ?? null) : null);
    });
    return () => {
      live = false;
    };
  }, [apiUrl, customerId, allowed, refreshKey]);

  const markSeen = useCallback(() => {
    if (!signal || !customerId || !digestShowsEveryNewNote(signal)) return;
    const through = seenThroughFor(signal);
    const token = readSession().token;
    if (!through || !token) return;
    void markCustomerNotesSeen(apiUrl, token, customerId, through);
  }, [apiUrl, customerId, signal]);

  return { signal, markSeen };
}

interface SignalsContextValue {
  signals: ReadonlyMap<string, ClientNoteSignal>;
  /** The row whose digest is open (one at a time per page). */
  expandedRow: string | null;
  /** Rows opened during this page view (MAC10: their chip stays to reopen). */
  openedRows: ReadonlySet<string>;
  toggleRow: (rowKey: string, customerId: string) => void;
  collapse: () => void;
}

const SignalsContext = createContext<SignalsContextValue | null>(null);

export interface ClientNoteSignalsProviderProps {
  readonly apiUrl: string;
  readonly customerIds: ReadonlyArray<string | null | undefined>;
  /** The page's own refresh key; a change refetches the signals. */
  readonly refreshKey?: unknown;
  readonly children: ReactNode;
}

export function ClientNoteSignalsProvider({
  apiUrl,
  customerIds,
  refreshKey,
  children,
}: ClientNoteSignalsProviderProps): JSX.Element {
  const [signals, setSignals] = useState<ReadonlyMap<string, ClientNoteSignal>>(() => new Map());
  const [expandedRow, setExpandedRow] = useState<string | null>(null);
  const [openedRows, setOpenedRows] = useState<ReadonlySet<string>>(() => new Set());
  const allowed = canReadClientNotes();
  // A stable key for the id SET, so a re-render with the same rows is free.
  const idKey = useMemo(
    () =>
      [...new Set(customerIds.filter((id): id is string => typeof id === 'string' && id.length > 0))]
        .sort()
        .join(','),
    [customerIds],
  );

  useEffect(() => {
    if (!allowed || idKey.length === 0) {
      setSignals(new Map());
      return;
    }
    let live = true;
    void fetchClientNoteSignals(apiUrl, idKey.split(',')).then((r) => {
      if (!live) return;
      setSignals(r.kind === 'ok' ? new Map(r.signals.map((s) => [s.customer_id, s])) : new Map());
    });
    return () => {
      live = false;
    };
  }, [apiUrl, idKey, allowed, refreshKey]);

  const markSeen = useCallback(
    (customerId: string) => {
      const signal = signals.get(customerId);
      if (!signal || !digestShowsEveryNewNote(signal)) return;
      const through = seenThroughFor(signal);
      const token = readSession().token;
      if (!through || !token) return;
      // Optimistic: the chip clears on every row of this client now.
      setSignals((prev) => {
        const next = new Map(prev);
        const cur = next.get(customerId);
        if (cur) next.set(customerId, { ...cur, unseen_count: 0 });
        return next;
      });
      void markCustomerNotesSeen(apiUrl, token, customerId, through);
    },
    [apiUrl, signals],
  );

  const toggleRow = useCallback(
    (rowKey: string, customerId: string) => {
      setExpandedRow((cur) => (cur === rowKey ? null : rowKey));
      if (expandedRow !== rowKey) {
        setOpenedRows((prev) => (prev.has(rowKey) ? prev : new Set(prev).add(rowKey)));
        markSeen(customerId);
      }
    },
    [expandedRow, markSeen],
  );

  const value = useMemo<SignalsContextValue>(
    () => ({ signals, expandedRow, openedRows, toggleRow, collapse: () => setExpandedRow(null) }),
    [signals, expandedRow, openedRows, toggleRow],
  );

  return <SignalsContext.Provider value={value}>{children}</SignalsContext.Provider>;
}

export interface ClientNoteChipProps {
  readonly customerId: string | null | undefined;
  readonly variant: 'toggle' | 'link';
  /** Toggle variant: the row this chip opens. */
  readonly rowKey?: string;
}

export function ClientNoteChip({ customerId, variant, rowKey }: ClientNoteChipProps): JSX.Element | null {
  const ctx = useContext(SignalsContext);
  if (!ctx || !customerId) return null;
  const signal = ctx.signals.get(customerId);
  const isToggle = variant === 'toggle' && rowKey !== undefined;
  const expanded = isToggle && ctx.expandedRow === rowKey;
  // MAC10: the opened row keeps its chip so focus never lands on nothing.
  const label = isToggle
    ? toggleChipLabel(signal, { expanded, opened: ctx.openedRows.has(rowKey) })
    : clientNoteChipLabel(signal);
  if (!label) return null;
  const tone = signal && signal.unseen_count > 0 ? 'new' : 'pinned';
  const className = `ferro-clientnote-chip ferro-clientnote-chip--${tone}`;
  const testId = `client-note-chip-${customerId}`;

  if (variant === 'link') {
    return (
      <Link className={className} to={`/customers/${customerId}`} data-testid={testId}>
        {label}
      </Link>
    );
  }
  const key = rowKey ?? customerId;
  const onKeyDown = (e: KeyboardEvent<HTMLButtonElement>): void => {
    if (e.key === 'Escape' && expanded) {
      e.preventDefault();
      ctx.collapse();
    }
  };
  return (
    <button
      type="button"
      className={className}
      aria-expanded={expanded}
      aria-controls={`client-notes-row-${key}`}
      onClick={() => ctx.toggleRow(key, customerId)}
      onKeyDown={onKeyDown}
      data-testid={testId}
    >
      {label}
    </button>
  );
}

export interface ClientNotesRowSlotProps {
  readonly customerId: string | null | undefined;
  readonly rowKey: string;
}

/** The open row's digest (pinned + new to me, at most 3 each). */
export function ClientNotesRowSlot({ customerId, rowKey }: ClientNotesRowSlotProps): JSX.Element | null {
  const ctx = useContext(SignalsContext);
  if (!ctx || !customerId || ctx.expandedRow !== rowKey) return null;
  const signal = ctx.signals.get(customerId);
  if (!signal) return null;
  return (
    <div
      id={`client-notes-row-${rowKey}`}
      className="ferro-clientnotes__row-slot"
      data-testid={`client-notes-row-${rowKey}`}
    >
      <ClientNotesDigest
        pinned={signal.pinned}
        newNotes={signal.new_notes}
        pinnedTotal={signal.pinned_total}
        // The digest counts what is still unseen from the SERVER's answer:
        // after an optimistic "seen" the list stays on screen (tagged New
        // for this view) and the footer offers "Open client".
        unseenCount={signal.new_notes.length >= signal.unseen_count ? signal.new_notes.length : signal.unseen_count}
        customerId={customerId}
        mode="pinned-and-new"
      />
    </div>
  );
}
